import { useEffect, useRef, useState } from 'react'
import { jobs as jobsApi, type JobStatus, type JobProgressEvent } from '../lib/api'

interface JobProgressState {
  status: JobStatus | null
  phase: string | null
  progress: number
  isComplete: boolean
  error: string | null
}

export function useJobProgress(jobId: string | null): JobProgressState {
  const [state, setState] = useState<JobProgressState>({
    status: null,
    phase: null,
    progress: 0,
    isComplete: false,
    error: null,
  })

  const closeRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (!jobId) return

    // Clean up any previous connection
    closeRef.current?.()

    const close = jobsApi.streamJobProgress(
      jobId,
      (event: JobProgressEvent) => {
        setState({
          status: event.status,
          phase: event.phase,
          progress: event.progress,
          isComplete: event.type === 'complete',
          error: event.type === 'error' ? (event.message ?? 'Unknown error') : null,
        })
      },
      (err: Error) => {
        setState((prev) => ({
          ...prev,
          error: err.message,
        }))
      }
    )

    closeRef.current = close

    return () => {
      close()
      closeRef.current = null
    }
  }, [jobId])

  return state
}
